/**
 * Handler: purchase event
 */

import type { WindowAnalyzify } from '../../types/window-analyzify';
import type { StorefrontOrder } from '../../types/storefront';
import { adaptOrder } from '../../adapters/order';
import { generateEventId } from '../utils';

export function handlePurchase(
  analyzify: WindowAnalyzify,
  order: StorefrontOrder
): void {
  const adaptedOrder = adaptOrder(order);
  const eventId = generateEventId();
  const props = analyzify.properties;

  // Convert cents to dollars for platform APIs
  const value = adaptedOrder.total_price / 100;
  const tax = adaptedOrder.total_tax / 100;
  const shipping = adaptedOrder.total_shipping / 100;

  // Build GA4-compatible items array
  const ga4Items = adaptedOrder.items.map((item, index) => ({
    item_id: item.sku || item.product_id.toString(),
    item_name: item.title,
    item_brand: item.vendor,
    item_category: item.product_type,
    price: item.price / 100,
    quantity: item.quantity,
    index,
  }));

  // GA4 - call gtag() directly, routed per-property via send_to (primary + secondary)
  if (typeof (window as any).gtag === 'function' && props?.GA4?.status) {
    const ga4Params = {
      transaction_id: adaptedOrder.id,
      value,
      tax,
      shipping,
      currency: adaptedOrder.currency,
      items: ga4Items,
    };
    if (props.GA4.primary?.status && props.GA4.primary.events?.purchase && props.GA4.primary.id) {
      (window as any).gtag('event', 'purchase', { ...ga4Params, send_to: props.GA4.primary.id });
    }
    if (props.GA4.secondary?.status && props.GA4.secondary.events?.purchase && props.GA4.secondary.id) {
      (window as any).gtag('event', 'purchase', { ...ga4Params, send_to: props.GA4.secondary.id });
    }
  }

  // Google Ads - call gtag() for conversion
  if (typeof (window as any).gtag === 'function') {
    if (props?.GADS?.status && props.GADS.primary?.conversions?.purchase?.status) {
      const conversionId = props.GADS.primary.conversions.purchase.value;
      const gadsId = props.GADS.primary.id;
      if (conversionId && gadsId) {
        (window as any).gtag('event', 'conversion', {
          send_to: `${gadsId}/${conversionId}`,
          value,
          currency: adaptedOrder.currency,
          transaction_id: adaptedOrder.id,
        });
      }
    }
  }

  // Facebook - call fbq() directly
  if (typeof (window as any).fbq === 'function') {
    if (props?.FACEBOOK?.status && props.FACEBOOK.primary?.events?.purchase) {
      const contents = adaptedOrder.items.map((item) => ({
        id: item.sku || item.product_id.toString(),
        quantity: item.quantity,
        item_price: item.price / 100,
      }));

      (window as any).fbq('track', 'Purchase', {
        value,
        currency: adaptedOrder.currency,
        content_type: 'product_group',
        contents,
        content_ids: contents.map((c) => c.id),
        num_items: adaptedOrder.items.reduce((sum, item) => sum + item.quantity, 0),
      }, {
        eventID: eventId,
      });
    }
  }

  // TikTok - call ttq.track() directly
  const ttq = (window as any).ttq;
  if (ttq && typeof ttq.track === 'function') {
    if (props?.TIKTOK?.status && props.TIKTOK.events?.purchase) {
      const contents = adaptedOrder.items.map((item) => ({
        content_id: item.sku || item.product_id.toString(),
        content_name: item.title,
        quantity: item.quantity,
        price: item.price / 100,
      }));

      try {
        ttq.track('CompletePayment', {
          value,
          currency: adaptedOrder.currency,
          contents,
          content_type: 'product',
        }, {
          event_id: eventId,
        });
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        analyzify.log?.(msg, 'dispatcher', 'ttq.purchase');
      }
    }
  }

  // GTM - push to dataLayer
  if (Array.isArray((window as any).dataLayer)) {
    if (props?.GTM?.status) {
      (window as any).dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: adaptedOrder.id,
          value,
          tax,
          shipping,
          currency: adaptedOrder.currency,
          items: ga4Items,
        },
      });
    }
  }

  // Pinterest - call pintrk() directly
  if (typeof (window as any).pintrk === 'function') {
    if (props?.PINTEREST?.status && props.PINTEREST.events?.purchase) {
      (window as any).pintrk('track', 'checkout', {
        value,
        currency: adaptedOrder.currency,
        order_id: adaptedOrder.id,
        order_quantity: adaptedOrder.items.reduce((sum, item) => sum + item.quantity, 0),
        line_items: adaptedOrder.items.map((item) => ({
          product_id: item.product_id.toString(),
          product_name: item.title,
          product_price: item.price / 100,
          product_quantity: item.quantity,
        })),
      });
    }
  }

  // Bing/Microsoft - push to uetq
  const uetq = (window as any).uetq;
  if (Array.isArray(uetq)) {
    if (props?.BING?.status && props.BING.events?.purchase) {
      uetq.push('event', 'purchase', {
        revenue_value: value,
        currency: adaptedOrder.currency,
      });
    }
  }

  // Snapchat - call snaptr() directly
  if (typeof (window as any).snaptr === 'function') {
    if (props?.SNAPCHAT?.status && props.SNAPCHAT.events?.purchase) {
      (window as any).snaptr('track', 'PURCHASE', {
        price: value,
        currency: adaptedOrder.currency,
        transaction_id: adaptedOrder.id,
        item_ids: adaptedOrder.items.map((item) => item.sku || item.product_id.toString()),
        number_items: adaptedOrder.items.reduce((sum, item) => sum + item.quantity, 0),
      });
    }
  }

  // Criteo - push to criteo_q
  const criteoQ = (window as any).criteo_q;
  if (Array.isArray(criteoQ)) {
    if (props?.CRITEO?.status && props.CRITEO.events?.purchase) {
      criteoQ.push({
        event: 'trackTransaction',
        id: adaptedOrder.id,
        item: adaptedOrder.items.map((item) => ({
          id: item.product_id.toString(),
          price: item.price / 100,
          quantity: item.quantity,
        })),
      });
    }
  }

  // Klaviyo - push to _learnq
  const learnq = (window as any)._learnq;
  if (learnq && typeof learnq.push === 'function') {
    if (props?.KLAVIYO?.status && props.KLAVIYO.events?.purchase) {
      learnq.push(['track', 'Placed Order', {
        $event_id: eventId,
        $value: value,
        OrderId: adaptedOrder.id,
        Categories: [...new Set(adaptedOrder.items.map((item) => item.product_type))],
        ItemNames: adaptedOrder.items.map((item) => item.title),
        Items: adaptedOrder.items.map((item) => ({
          ProductID: item.product_id.toString(),
          SKU: item.sku,
          ProductName: item.title,
          Quantity: item.quantity,
          ItemPrice: item.price / 100,
          ProductCategories: [item.product_type],
        })),
      }]);
    }
  }

  analyzify.log('Purchase tracked across all enabled platforms', 'dispatcher');

  // Dispatch custom event for developers
  analyzify.dispatchEvent('analyzify:purchase', {
    order: adaptedOrder,
    eventId,
  });
}
